import { autoUpdate, flip, FloatingFocusManager, FloatingPortal, offset, shift, useDismiss, useFloating, useInteractions, useRole } from '@floating-ui/react';
import React, { useEffect, useState } from 'react';
import { FOLLOW_PRESET_CONFIG } from '../../constants';
import { captureSilentError } from '../../services/posthog';
import { FollowPreset, ShoutFollow, User } from '../../types';

interface FollowPopoverProps {
    friend: User;
    currentFollow: ShoutFollow | undefined;
    onFollow: (preset: FollowPreset) => Promise<void>;
    onUnfollow: () => Promise<void>;
    onUpdatePreset: (preset: FollowPreset) => Promise<void>;
    isOpen: boolean;
    onClose: () => void;
    anchorRef: React.RefObject<HTMLButtonElement>;
}

export const FollowPopover: React.FC<FollowPopoverProps> = ({ 
    friend, 
    currentFollow, 
    onFollow, 
    onUnfollow, 
    onUpdatePreset,
    isOpen,
    onClose,
    anchorRef
}) => {
    const [loading, setLoading] = useState(false);

    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: (open) => {
            if (!open) onClose();
        },
        placement: 'bottom-end',
        middleware: [
            offset(8),
            flip(),
            shift({ padding: 8 })
        ],
        whileElementsMounted: autoUpdate,
    });

    const dismiss = useDismiss(context);
    const role = useRole(context);

    const { getFloatingProps } = useInteractions([
        dismiss,
        role,
    ]);

    // Sync external anchor ref with floating ui
    useEffect(() => {
        if (anchorRef.current) {
            refs.setReference(anchorRef.current);
        }
    }, [anchorRef, refs, isOpen]);

    if (!isOpen) return null;

    const isValidPreset = (value: string): value is FollowPreset => 
        value in FOLLOW_PRESET_CONFIG;

    const handleSelect = async (preset: FollowPreset) => {
        if (!isValidPreset(preset)) {
             captureSilentError('FollowPopover.handleSelect', new Error(`Invalid follow preset: ${preset}`));
             return;
        }

        setLoading(true);
        try {
            if (currentFollow) {
                if (currentFollow.notifyPreset !== preset) {
                    await onUpdatePreset(preset);
                }
            } else {
                await onFollow(preset);
            }
            onClose();
        } finally {
            setLoading(false);
        }
    };

    const handleUnfollow = async () => {
        setLoading(true);
        try {
            await onUnfollow();
            onClose();
        } finally {
            setLoading(false);
        }
    };

    return (
        <FloatingPortal>
            <FloatingFocusManager context={context} modal={false}>
                <div
                    ref={refs.setFloating}
                    style={floatingStyles}
                    className="z-50 bg-white rounded-xl shadow-xl border border-brand-cream-dark w-64 p-3 outline-none animate-in fade-in zoom-in-95 duration-200"
                    {...getFloatingProps()}
                    onClick={(e) => e.stopPropagation()}
                >
                     <div className="text-sm font-medium text-brand-dark-light mb-2 px-2">
                        Get notified about {friend.displayName.split(' ')[0]}'s:
                    </div>

                    <div className="space-y-1">
                        {(Object.keys(FOLLOW_PRESET_CONFIG) as FollowPreset[]).map((presetKey) => {
                            const config = FOLLOW_PRESET_CONFIG[presetKey];
                            const PresetIcon = config.Icon;
                            const isSelected = currentFollow?.notifyPreset === presetKey;

                            return (
                                <button
                                    key={presetKey}
                                    onClick={() => handleSelect(presetKey)}
                                    disabled={loading}
                                    className={`w-full text-left px-2 py-2 rounded-lg text-sm transition-colors flex items-center gap-2
                                        ${isSelected 
                                            ? 'bg-brand-cream-dark text-brand-dark' 
                                            : 'hover:bg-brand-cream text-brand-dark'
                                        }`}
                                >
                                    <PresetIcon size={16} strokeWidth={2.5} className="shrink-0 text-brand-red" />
                                    <div>
                                        <div className="font-medium leading-none mb-0.5">{config.label}</div>
                                        <div className="text-[10px] opacity-70 leading-none">{config.description}</div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    {currentFollow && (
                        <>
                            <div className="h-px bg-brand-cream-dark my-2" />
                            <button
                                onClick={handleUnfollow}
                                disabled={loading}
                                className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-medium text-brand-red hover:bg-brand-red/10 transition-colors"
                            >
                                Turn Off Notifications
                            </button>
                        </>
                    )}
                </div>
            </FloatingFocusManager>
        </FloatingPortal>
    );
};
